/**
 * useClimateStats Hook
 *
 * React hook for loading climate statistics for a weather file.
 * Loads monthly climate data and computes annual statistics.
 */

import { useState, useEffect, useCallback } from 'react';
import { getFileClimateData } from '../data/weatherFileIndex';
import type { WeatherFileMetadata, ClimateStats, MonthlyClimateEntry } from '../types';

export interface UseClimateStatsState {
  /** Computed annual climate statistics */
  stats: ClimateStats | null;
  /** Monthly climate data */
  monthlyData: MonthlyClimateEntry[] | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
}

export interface UseClimateStatsActions {
  /** Load climate data */
  loadClimateStats: () => Promise<void>;
}

export type UseClimateStatsReturn = UseClimateStatsState & UseClimateStatsActions;

/**
 * Compute annual ClimateStats from monthly data.
 */
function computeAnnualStats(monthly: MonthlyClimateEntry[]): ClimateStats {
  if (monthly.length === 0) {
    return {};
  }

  // Calculate weighted averages (by days in month approximately)
  // For simplicity, we'll use simple averages for avgTemp, avgGHI, avgDNI, avgWindSpeed
  const avgTemp = monthly.reduce((sum, m) => sum + m.avgTemp, 0) / monthly.length;
  const minTemp = Math.min(...monthly.map((m) => m.minTemp));
  const maxTemp = Math.max(...monthly.map((m) => m.maxTemp));

  // Sum degree days for the year
  const hdd18 = monthly.reduce((sum, m) => sum + m.hdd, 0);
  const cdd10 = monthly.reduce((sum, m) => sum + m.cdd, 0);

  // Average solar radiation
  const avgGHI = monthly.reduce((sum, m) => sum + m.avgGHI, 0) / monthly.length;
  const avgDNI = monthly.reduce((sum, m) => sum + m.avgDNI, 0) / monthly.length;

  // Average wind speed
  const avgWindSpeed = monthly.reduce((sum, m) => sum + m.avgWindSpeed, 0) / monthly.length;

  // Average humidity
  const avgHumidity = monthly.reduce((sum, m) => sum + m.avgHumidity, 0) / monthly.length;

  return {
    avgTemp,
    minTemp,
    maxTemp,
    hdd18,
    cdd10,
    avgGHI,
    avgDNI,
    avgWindSpeed,
    avgHumidity,
  };
}

/**
 * Hook for loading and computing climate statistics for a weather file.
 */
export function useClimateStats(weatherFile: WeatherFileMetadata | null): UseClimateStatsReturn {
  const [stats, setStats] = useState<ClimateStats | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyClimateEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when weather file changes
  useEffect(() => {
    setStats(null);
    setMonthlyData(null);
    setError(null);
  }, [weatherFile?.id]);

  // Load climate data
  const loadClimateStats = useCallback(async () => {
    if (!weatherFile) {
      setError('No weather file selected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get country code from the weather file
      const countryCode = weatherFile.location.countryCode;

      // Fetch climate data for this file
      const climateData = await getFileClimateData(weatherFile.id, countryCode);

      if (climateData && climateData.length > 0) {
        setMonthlyData(climateData);
        // Compute annual stats from monthly data
        const computedStats = computeAnnualStats(climateData);
        setStats(computedStats);
      } else {
        // No climate data available for this file
        setStats(null);
        setMonthlyData(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load climate data';
      setError(message);
      setStats(null);
      setMonthlyData(null);
    } finally {
      setIsLoading(false);
    }
  }, [weatherFile]);

  return {
    stats,
    monthlyData,
    isLoading,
    error,
    loadClimateStats,
  };
}
